import { uniq } from 'ramda';

import { ERoom, type IFilter } from 'shared/types/filter';
import { type ILocation } from 'shared/types/locations';
import { type IResidentialComplex } from 'shared/types/residentials';
import { getParameterValueByQueryName } from 'shared/utils/url';

import { defaultFilterInitialState } from './defaultFilterInitialState';

interface IDefaultSetter {
  defaultSetter: TSetter;
}
type TFiltersKeys = keyof IFilter;
interface ISetterArguments {
  curr: TFiltersKeys;
  valueFromQueryParam: string | null;
  locationVariants: number[];
  locationId: number;
  acc: IFilter;
}
type TSetter = (value: string, args: ISetterArguments) => undefined | string | string[] | number | number[] | boolean;
type TFilterValueGetterKeys = { [key in TFiltersKeys]?: TSetter };

export const DEFAULT_DEADLINE_VALUE = 'any';

export const getStringToNumber = (value: string): number | undefined => (Number(value) ? Number(value) : undefined);

const filterValueGetter: TFilterValueGetterKeys & IDefaultSetter = {
  apartmentsWithDecoration: prepareBoolean,
  deadline: prepareDeadline,
  defaultSetter: (value: string) => value,
  location: prepareLocation,
  maxArea: getStringToNumber,
  maxPrice: getStringToNumber,
  minArea: getStringToNumber,
  minPrice: getStringToNumber,
  readyApartments: prepareBoolean,
  rooms: prepareRooms,
};

interface IGetFilterFromUrlArguments {
  url: string;
  deadlineVariants: number[];
  locationVariants: number[];
  locationId: number;
}

export function getFiltersFromUrl({
  url,
  deadlineVariants,
  locationVariants,
  locationId,
}: IGetFilterFromUrlArguments): IFilter {
  const filterKeys = Object.keys(defaultFilterInitialState) as TFiltersKeys[];
  const filters = filterKeys.reduce((acc, curr) => {
    const valueFromQueryParam = getParameterValueByQueryName(curr, url);
    const setValue = filterValueGetter[curr] || filterValueGetter.defaultSetter;
    const allArgs = { acc, curr, locationId, locationVariants, valueFromQueryParam };

    // Подстановка locationId из сабдомена при отсутствии в query string
    if (curr === 'location' && locationId && !valueFromQueryParam) {
      return {
        ...acc,
        location: prepareLocation(String(locationId), {
          ...allArgs,
          locationId: defaultFilterInitialState.location,
        }),
      };
    }

    return {
      ...acc,
      [curr]: valueFromQueryParam ? setValue(valueFromQueryParam, allArgs) : defaultFilterInitialState[curr],
    };
  }, defaultFilterInitialState);

  return { ...filters, deadlineVariants, locationVariants };
}

export function getDeadlineVariants(residentialComplexes?: IResidentialComplex[] | null): number[] {
  if (!residentialComplexes) {
    return [];
  }

  const duplicatedYears = residentialComplexes.reduce<number[]>((acc, curr) => {
    const years = curr.finishInfo?.years;

    if (years) {
      return acc.concat(years);
    }

    return acc;
  }, []);

  return Array.from(uniq(duplicatedYears)).sort((a, b) => a - b);
}

export function getLocationVariants(locations?: ILocation[] | null, availableRegions: number[] = []): number[] {
  if (!locations) {
    return [];
  }

  const filteredLocations = locations.filter(location => availableRegions.includes(location.locationId ?? -1000));

  return filteredLocations.reduce((acc, curr) => {
    if (!curr.locationId || !curr.jkIds?.length) {
      return acc;
    }

    return [...acc, curr.locationId];
  }, Array.of<number>());
}

function prepareLocation(value: string, { locationId, locationVariants }: ISetterArguments): number {
  const numberedId = Number(value);
  const isIdExists = locationVariants.includes(numberedId);

  if (isIdExists) {
    return numberedId;
  }

  return locationId;
}

function prepareRooms(value: string): string[] {
  const roomKeys = Object.values(ERoom);
  const rooms = value.split(',');
  const isValidRooms = rooms.find(room => Boolean(roomKeys.includes(room as ERoom)));

  if (!isValidRooms) {
    return [];
  }

  return value.split(',');
}

function prepareDeadline(value: string): number | undefined {
  return value === DEFAULT_DEADLINE_VALUE ? undefined : getStringToNumber(value);
}

function prepareBoolean(value: string): boolean {
  return value === 'true';
}
